'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { BENEFIT_CONSENT_TITLE, createMarketingConsentDialog, MarketingConsentSheet } from '@/app/_components/MarketingConsentSheet';
import { hasFullMarketingConsent, useAcceptMarketingConsent } from '@/hooks/useAcceptMarketingConsent';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { closeResponsiveDialog, tryOpenResponsiveDialog, useResponsiveDialogStore } from '@/stores/ResponsiveDialogStore';
const BENEFIT_CONSENT_SESSION_KEY_PREFIX = 'benefit-consent-shown';
const isBenefitPathname = (pathname: string): boolean => pathname === '/benefit' || pathname.startsWith('/benefit/');
export const BenefitEntryConsentController = () => {
  const pathname = usePathname();
  const isLogin = useAuthStore(state => state.isLogin);
  const myInfo = useAuthStore(state_0 => state_0.myInfo);
  const isDialogOpen = useResponsiveDialogStore(state_1 => state_1.isOpen);
  const isAppModalOpened = useDeviceInfoStore(state_2 => state_2.appModal.isOpened);
  const isDeviceInfoMounted = useDeviceInfoStore(state_3 => state_3.isMounted);
  const webviewType = useDeviceInfoStore(state_4 => state_4.webviewType);
  const appVersion = useDeviceInfoStore(state_5 => state_5.appVersion);
  const acceptConsent = useAcceptMarketingConsent();
  const shownMemberKeysRef = useRef(new Set<string>());
  useEffect(() => {
    const memberKey = myInfo?.memberCode ?? myInfo?.id?.toString();
    const isEligible = isLogin === true && Boolean(memberKey) && myInfo?.age !== undefined && myInfo.age >= 19 && !hasFullMarketingConsent(myInfo.notificationOptions);

    //* 앱 모달(권한·강제 업데이트)에 양보한다 — store 밖에서 뜨는 별도 ResponsiveDialog라
    //* tryOpen의 isOpen 검사에 걸리지 않고, 권한·강제 업데이트는 사용자가 닫을 수 없다.
    //* 앱에서는 버전 응답까지 기다린다 — 먼저 열면 뒤늦게 도착한 forceUpdate와 겹친다.
    const isAppStartupResolved = isDeviceInfoMounted && (webviewType !== WEBVIEW_TYPE.WELLO_APP || Boolean(appVersion));
    if (!isBenefitPathname(pathname) || !isEligible || isDialogOpen || !isAppStartupResolved || isAppModalOpened) {
      return;
    }
    const sessionKey = `${BENEFIT_CONSENT_SESSION_KEY_PREFIX}:${memberKey}`;
    let hasShown = shownMemberKeysRef.current.has(sessionKey);
    try {
      hasShown = hasShown || sessionStorage.getItem(sessionKey) === 'true';
    } catch {
      //* 저장소 접근이 막혀도 현재 페이지 수명 동안의 중복 노출은 ref로 방지한다.
    }
    if (hasShown) {
      return;
    }

    //* 같은 effect 주기에 다른 dialog가 열릴 수 있으므로 현재 effect 묶음이 끝난 뒤
    //* store를 원자적으로 다시 확인한다. 실패하면 노출 기록을 남기지 않아 close 후 재시도한다.
    const timeoutId = window.setTimeout(() => {
      const hasOpened = tryOpenResponsiveDialog(createMarketingConsentDialog({
        title: BENEFIT_CONSENT_TITLE,
        agreeLabel: '동의하기',
        onAgree: () => acceptConsent.mutateAsync(undefined).then(() => {
          //* 동의 성공으로 자동 출석이 재무장되므로, 이 then이 도는 사이 출석 완료
          //* 시트가 자리를 차지했을 수 있다. 내가 연 동의창일 때만 닫는다.
          if (useResponsiveDialogStore.getState().currentDialog?.component === MarketingConsentSheet) {
            closeResponsiveDialog();
          }
        })
      }));
      if (!hasOpened) {
        return;
      }
      shownMemberKeysRef.current.add(sessionKey);
      try {
        sessionStorage.setItem(sessionKey, 'true');
      } catch {
        //* private browsing 등에서는 in-memory 세션 게이트만 유지한다.
      }
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [acceptConsent, appVersion, isAppModalOpened, isDeviceInfoMounted, isDialogOpen, isLogin, myInfo, pathname, webviewType]);
  return null;
};